import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Team, InstanceMessage, CreateTeamDto, SendMessageDto } from '../types/team';

// Queries

export function useTeams(projectId: string | undefined) {
  return useQuery<Team[]>({
    queryKey: ['projects', projectId, 'teams'],
    queryFn: async () => {
      const res = await api.get(`/api/projects/${projectId}/teams`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useTeam(id: string | undefined) {
  return useQuery<Team>({
    queryKey: ['teams', id],
    queryFn: async () => {
      const res = await api.get(`/api/teams/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useTeamMessages(teamId: string | undefined, limit?: number) {
  return useQuery<InstanceMessage[]>({
    queryKey: ['teams', teamId, 'messages', { limit }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (limit !== undefined) params.set('limit', String(limit));
      const res = await api.get(`/api/teams/${teamId}/messages?${params.toString()}`);
      // Backend returns { messages[], total, limit, offset }
      return res.data.messages ?? res.data;
    },
    enabled: !!teamId,
    refetchInterval: 3000,
  });
}

// Mutations

export function useCreateTeam(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTeamDto) => {
      const res = await api.post(`/api/projects/${projectId}/teams`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'teams'] });
    },
  });
}

export function useStopTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/teams/${id}/stop`);
      return res.data;
    },
    onSuccess: (data: Team) => {
      queryClient.invalidateQueries({ queryKey: ['teams', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'teams'] });
    },
  });
}

export function usePauseTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/teams/${id}/pause`);
      return res.data;
    },
    onSuccess: (data: Team) => {
      queryClient.invalidateQueries({ queryKey: ['teams', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'teams'] });
    },
  });
}

export function useResumeTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/teams/${id}/resume`);
      return res.data;
    },
    onSuccess: (data: Team) => {
      queryClient.invalidateQueries({ queryKey: ['teams', data.id] });
      queryClient.invalidateQueries({ queryKey: ['projects', data.projectId, 'teams'] });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/teams/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useSendMessage(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SendMessageDto) => {
      const res = await api.post(`/api/teams/${teamId}/messages`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'messages'] });
    },
  });
}
